/**
 * LifeFunctionSounds - Audio feedback for life function events
 * Creates procedural sounds for metabolism, reproduction, response, etc.
 */

import { LifeFunction, StimulusType } from '@/game/systems/LifeFunctionSystem';

/**
 * Configuration for life function sounds
 */
export interface LifeFunctionSoundsConfig {
  /** Master volume (0-1) */
  masterVolume?: number;
  /** Whether audio is enabled */
  enabled?: boolean;
}

const DEFAULT_CONFIG: Required<LifeFunctionSoundsConfig> = {
  masterVolume: 0.6,
  enabled: true,
};

/**
 * LifeFunctionSounds generates procedural audio for life function events
 */
export class LifeFunctionSounds {
  private audioContext: AudioContext | null = null;
  private config: Required<LifeFunctionSoundsConfig>;
  private masterGain: GainNode | null = null;

  constructor(config: LifeFunctionSoundsConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize the audio context (must be called after user interaction)
   */
  initialize(): void {
    if (this.audioContext) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = this.config.masterVolume;
    } catch (e) {
      console.warn('Web Audio API not available:', e);
    }
  }

  /**
   * Play sound for a life function event
   */
  playLifeFunctionSound(func: LifeFunction, data?: Record<string, unknown>): void {
    if (!this.config.enabled || !this.audioContext || !this.masterGain) return;

    switch (func) {
      case LifeFunction.METABOLISM:
        this.playMetabolismSound(data?.type as string);
        break;
      case LifeFunction.REPRODUCTION:
        this.playReproductionSound(data?.phase as string);
        break;
      case LifeFunction.RESPONSE_TO_STIMULI:
        this.playResponseSound(data?.stimulus as StimulusType);
        break;
      case LifeFunction.HOMEOSTASIS:
        this.playHomeostasisSound(data?.deviation as number);
        break;
      case LifeFunction.GROWTH:
        this.playGrowthSound();
        break;
      case LifeFunction.WASTE_REMOVAL:
        this.playWasteRemovalSound();
        break;
      case LifeFunction.GAS_EXCHANGE:
        this.playGasExchangeSound();
        break;
    }
  }

  // ============================================================================
  // METABOLISM SOUND
  // ============================================================================

  /**
   * Warm, pulsing energy sound for metabolism
   * 4 pulses at 220Hz harmonics
   */
  playMetabolismSound(_type?: string): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;
    const baseFreq = 220;

    // Create warm pulsing sound
    for (let i = 0; i < 4; i++) {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc.type = 'sine';
      osc.frequency.value = baseFreq * (1 + i * 0.5); // Harmonics

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15 / (i + 1), now + 0.05 + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2 + i * 0.15);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(now + i * 0.15);
      osc.stop(now + 0.4 + i * 0.15);
    }

    // Add subtle low rumble for "energy production"
    const rumble = this.audioContext.createOscillator();
    const rumbleGain = this.audioContext.createGain();

    rumble.type = 'sine';
    rumble.frequency.value = 55;

    rumbleGain.gain.setValueAtTime(0, now);
    rumbleGain.gain.linearRampToValueAtTime(0.1, now + 0.1);
    rumbleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

    rumble.connect(rumbleGain);
    rumbleGain.connect(this.masterGain);

    rumble.start(now);
    rumble.stop(now + 0.9);
  }

  // ============================================================================
  // REPRODUCTION SOUND
  // ============================================================================

  /**
   * Diverging tones for cell division
   * 440Hz splits to 523Hz and 349Hz with stereo panning
   */
  playReproductionSound(phase?: string): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;

    if (phase === 'division_start' || !phase) {
      // Initial unified tone
      const osc1 = this.audioContext.createOscillator();
      const gain1 = this.audioContext.createGain();
      const pan1 = this.audioContext.createStereoPanner();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(440, now);
      osc1.frequency.linearRampToValueAtTime(523, now + 0.8);

      pan1.pan.setValueAtTime(0, now);
      pan1.pan.linearRampToValueAtTime(0.8, now + 0.8);

      gain1.gain.setValueAtTime(0.2, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 1.0);

      osc1.connect(gain1);
      gain1.connect(pan1);
      pan1.connect(this.masterGain);

      osc1.start(now);
      osc1.stop(now + 1.1);

      // Second diverging tone
      const osc2 = this.audioContext.createOscillator();
      const gain2 = this.audioContext.createGain();
      const pan2 = this.audioContext.createStereoPanner();

      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(440, now);
      osc2.frequency.linearRampToValueAtTime(349, now + 0.8);

      pan2.pan.setValueAtTime(0, now);
      pan2.pan.linearRampToValueAtTime(-0.8, now + 0.8);

      gain2.gain.setValueAtTime(0.2, now);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.0);

      osc2.connect(gain2);
      gain2.connect(pan2);
      pan2.connect(this.masterGain);

      osc2.start(now);
      osc2.stop(now + 1.1);
    } else if (phase === 'division_complete') {
      // Completion sound - two satisfied tones
      this.playCompletionChime();
    }
  }

  private playCompletionChime(): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;
    const notes = [523, 659, 784]; // C5, E5, G5

    notes.forEach((freq, i) => {
      const osc = this.audioContext!.createOscillator();
      const gain = this.audioContext!.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;

      gain.gain.setValueAtTime(0, now + i * 0.1);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.1 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.4);

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.5);
    });
  }

  // ============================================================================
  // RESPONSE TO STIMULI SOUND
  // ============================================================================

  /**
   * Quick reactive chirp for stimulus response
   */
  playResponseSound(stimulus?: StimulusType): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;

    // Different sounds for different stimuli
    let baseFreq = 880;
    let waveType: OscillatorType = 'sine';

    switch (stimulus) {
      case StimulusType.LIGHT:
        baseFreq = 1200;
        waveType = 'sine';
        break;
      case StimulusType.CHEMICAL:
        baseFreq = 660;
        waveType = 'triangle';
        break;
      case StimulusType.TEMPERATURE:
        baseFreq = 440;
        waveType = 'sawtooth';
        break;
      case StimulusType.TOUCH:
        baseFreq = 1000;
        waveType = 'square';
        break;
      case StimulusType.FOOD:
        baseFreq = 550;
        waveType = 'sine';
        break;
    }

    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    const filter = this.audioContext.createBiquadFilter();

    osc.type = waveType;
    osc.frequency.setValueAtTime(baseFreq, now);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.5, now + 0.1);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.8, now + 0.2);

    filter.type = 'lowpass';
    filter.frequency.value = 2000;

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.3);
  }

  // ============================================================================
  // HOMEOSTASIS SOUND
  // ============================================================================

  /**
   * Balanced oscillation returning to center frequency
   */
  playHomeostasisSound(deviation?: number): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;
    const centerFreq = 440;
    const startFreq = centerFreq + (deviation ?? 20) * 5;

    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, now);

    // Oscillate back toward center
    for (let i = 0; i < 4; i++) {
      const t = now + i * 0.2;
      const dampening = Math.pow(0.5, i);
      const freqOffset = (startFreq - centerFreq) * dampening;
      osc.frequency.linearRampToValueAtTime(centerFreq + freqOffset * (i % 2 === 0 ? 1 : -0.5), t + 0.1);
    }
    osc.frequency.linearRampToValueAtTime(centerFreq, now + 0.9);

    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.0);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 1.1);
  }

  // ============================================================================
  // GROWTH SOUND
  // ============================================================================

  /**
   * Ascending, expanding sound for growth
   */
  playGrowthSound(): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;

    // Low to high sweep
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(110, now);
    osc.frequency.exponentialRampToValueAtTime(330, now + 0.5);

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.1);
    gain.gain.linearRampToValueAtTime(0.15, now + 0.4);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.7);

    // Add harmonics
    const osc2 = this.audioContext.createOscillator();
    const gain2 = this.audioContext.createGain();

    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(220, now);
    osc2.frequency.exponentialRampToValueAtTime(660, now + 0.5);

    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(0.08, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

    osc2.connect(gain2);
    gain2.connect(this.masterGain);

    osc2.start(now);
    osc2.stop(now + 0.6);
  }

  // ============================================================================
  // WASTE REMOVAL SOUND
  // ============================================================================

  /**
   * Bubbling, releasing sound for waste removal
   */
  playWasteRemovalSound(): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;

    // Create bubbling effect with multiple short tones
    for (let i = 0; i < 5; i++) {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc.type = 'sine';
      const freq = 300 + Math.random() * 200;
      osc.frequency.setValueAtTime(freq, now + i * 0.08);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.7, now + i * 0.08 + 0.1);

      gain.gain.setValueAtTime(0, now + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.1, now + i * 0.08 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.12);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.15);
    }
  }

  // ============================================================================
  // GAS EXCHANGE SOUND
  // ============================================================================

  /**
   * Breathing-like whoosh for gas exchange
   */
  playGasExchangeSound(): void {
    if (!this.audioContext || !this.masterGain) return;

    const now = this.audioContext.currentTime;

    // White noise filtered to sound like breathing
    const bufferSize = this.audioContext.sampleRate * 0.8;
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.audioContext.createBufferSource();
    noise.buffer = buffer;

    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(400, now);
    filter.frequency.linearRampToValueAtTime(800, now + 0.3);
    filter.frequency.linearRampToValueAtTime(300, now + 0.7);
    filter.Q.value = 1;

    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.08, now + 0.15);
    gain.gain.linearRampToValueAtTime(0.05, now + 0.4);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.75);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    noise.start(now);
    noise.stop(now + 0.8);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Set master volume
   */
  setVolume(volume: number): void {
    this.config.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.config.masterVolume;
    }
  }

  /**
   * Enable or disable audio
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Resume audio context (needed after user interaction)
   */
  async resume(): Promise<void> {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.masterGain = null;
    }
  }
}
